import { USMapsBoolean } from './boolean';
import { ImageSettings } from './image';

export class MapState implements ImageSettings {
  /**
   * The id of the state. This is the same as the id in the main_settings.state_specific array.
   */
  arrayId?: number;

  /**
   * Primarily for identification purposes. However, you can overwrite a state's name.
   */
  name?: string;

  /**
   * This will fill up the popup and can include HTML/CSS.
   * If it exists, it will overwrite main_settings.state_description.
   */
  description?: string;

  /**
   * The state's color. If it exists, it will overwrite main_settings.state_color.
   */
  color? = '#555148';

  /**
   * The state's hover color. If it exists, it will overwrite main_settings.state_hover_color.
   */
  hover_color? = '#555148';

  /**
   * The url when the state is clicked. (e.g. 'http://simplemaps.com').
   * If it exists, it will overwrite main_settings.state_url.
   */
  url?: string;

  /**
   * 'yes' makes the state invisible.
   */
  hide?: USMapsBoolean = 'no';

  /**
   * 'yes' will zoom into the state upon click instead of redirecting to the url.
   * If it exists, it will overwrite main_settings.all_states_zoomable
   */
  zoomable?: USMapsBoolean = 'yes';

  /**
   * The percentage of the canvas that a zoomed state should take up.
   * (e.g. '.9', would create a 10% border around the zoomed state).
   */
  zoom_percentage?: number = 0.9;

  /**
   * 'yes' will make hovering or clicking on a state have no effect.
   * If it exists, it will overwrite main_settings.all_states_inactive
   */
  inactive?: USMapsBoolean = 'no';

  /**
   * Override main_settings.popups for this state.
   */
  popup?: string;

  /**
   * The state's border color. Note, state borders overlap, so you should use the same color
   * for states that border each other. This is best used for small islands.
   */
  border_color?: string;

  /**
   * The url of the background image when hovered over.
   */
  image_hover_url?: string;

  /**
   * The size of the image when hovered over.
   */
  image_hover_size?: number | 'auto';

  /**
   * Image position when hovered over.
   */
  image_hover_position?: 'center' | 'repeat' | 'manual';

  /**
   * Image horizontal placement when hovered over.
   */
  image_hover_x?: number;

  /**
   * Image vertical placement when hovered over.
   */
  image_hover_y?: number;

  /**
   * Image background color when hovered over.
   */
  image_hover_color?: string;

  /**
   * The state's state code as defined in our database.
   */
  COC_StateCode?: string;

  /**
   * The url of the background image.
   */
  image_url?: string;

  /**
   * The size of the image. If:
   * 0 > image_size < 1 ... it will be treated as a fraction of the state's bounding box.
   * image_size > 1 ... it will be treated as the width, in pixels, of the image.
   * image_size is 'auto' ... it will be chosen automatically based on image_position.
   */
  image_size?: number | 'auto';

  /**
   * By default, 'center', places the image in the center and will always fill the state.
   * 'manual' allows you to set placement via image_x and image_y.
   * 'repeat' will repeat as image_size allows.
   */
  image_position?: 'center' | 'repeat' | 'manual';

  /**
   * If image_position: 'manual', adjust the horizontal placement of the image
   * as a fraction of the state's bounding box width.
   */
  image_x?: number;

  /**
   * If image_position: 'manual', adjust the vertical placement of the image
   * as a fraction of the state's bounding box height.
   */
  image_y?: number;

  /**
   * If the aspect ratio of the image does not match the aspect ratio of the state's bounding box
   * (and the image isn't large enough), some of the state will remain uncovered.
   * This overwrites the default background color of color.
   */
  image_color?: string;

  constructor(state: MapState) {
    Object.assign(this, state);
  }
}
